import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileAPI, chatAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { getAuthToken, getStoredUser } from '../services/auth';
import { useCommunityConfig } from '../services/communityConfig';
import { ArrowLeft, Plus, Building2, Shield, Clock, CheckCircle2 } from 'lucide-react';
import { ProfileCard } from '../components/profile/ProfileCard';
import { FamilyCard, PetsCard, DailyHelpCard, VehiclesCard, HobbiesCard, DirectoryOptInCard } from '../components/profile/ResidentSections';
import { ChatPreferenceCard } from '../components/resident/SocialModals';
import { PasskeyCard } from '../components/profile/PasskeyCard';
import { FamilyModal, PetModal, DailyHelpModal, VehicleModal, CommunityModal } from '../components/profile/ProfileModals';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { config } = useCommunityConfig();
  const [user] = useState(getStoredUser());
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pictureUrl, setPictureUrl] = useState(null);

  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showPetModal, setShowPetModal] = useState(false);
  const [showCommunityModal, setShowCommunityModal] = useState(false);

  const [familyForm, setFamilyForm] = useState({ name: '', phone: '' });
  const [staffSearch, setStaffSearch] = useState('');
  const [staffResults, setStaffResults] = useState([]);
  const [vehicleForm, setVehicleForm] = useState({ vehicle_type: '4-wheeler', make: '', model: '', color: '', license_plate: '', is_ev: false });
  const [petForm, setPetForm] = useState({ pet_type: 'dog', breed: '', name: '' });
  const [communitySearch, setCommunitySearch] = useState('');
  const [communityResults, setCommunityResults] = useState([]);
  const [communities, setCommunities] = useState({ current: null, pending: [], approved: [] });

  const [hobbies, setHobbies] = useState([]);
  const [predefinedHobbies, setPredefinedHobbies] = useState([]);
  const [showInDirectory, setShowInDirectory] = useState(false);
  const [directoryVisibility, setDirectoryVisibility] = useState({ profile_picture: true, email: false, phone: false });
  const [allowMessages, setAllowMessages] = useState(true);

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await profileAPI.get();
      setProfile(data);
      if (data.profile_picture) {
        try { const blob = await profileAPI.getPictureBlob(data.profile_picture); setPictureUrl(URL.createObjectURL(blob.data)); }
        catch (err) { process.env.NODE_ENV !== 'production' && console.error('Failed to load profile picture:', err); setPictureUrl(null); }
      }
    } catch (err) { process.env.NODE_ENV !== 'production' && console.error('Failed to load profile:', err); toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  }, []);

  const loadCommunities = useCallback(async () => {
    try { const { data } = await profileAPI.getCommunities(); setCommunities(data); }
    catch (err) { process.env.NODE_ENV !== 'production' && console.error('Failed to load communities:', err); }
  }, []);

  const loadHobbies = useCallback(async () => {
    try {
      const [listRes, predefinedRes, prefRes] = await Promise.all([
        profileAPI.getHobbies(),
        profileAPI.getPredefinedHobbies(),
        profileAPI.getDirectoryPreference(),
      ]);
      setHobbies(listRes.data || []);
      setPredefinedHobbies(predefinedRes.data?.hobbies || []);
      setShowInDirectory(!!prefRes.data?.show_in_directory);
      if (prefRes.data?.directory_visibility) setDirectoryVisibility(prefRes.data.directory_visibility);
      try {
        const cp = await chatAPI.getPreferences();
        setAllowMessages(!!cp.data?.allow_direct_messages);
      } catch { /* silent */ }
    } catch (err) {
      process.env.NODE_ENV !== 'production' && console.error('Failed to load hobbies/directory:', err);
    }
  }, []);

  useEffect(() => { loadProfile(); loadCommunities(); loadHobbies(); }, [loadProfile, loadCommunities, loadHobbies]);
  useEffect(() => { return () => { if (pictureUrl) URL.revokeObjectURL(pictureUrl); }; }, [pictureUrl]);

  const handleUploadPicture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { toast.error('Only JPEG, PNG, or WebP images allowed'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    const formData = new FormData();
    formData.append('file', file);
    try { await profileAPI.uploadPicture(formData); toast.success('Profile picture updated'); loadProfile(); }
    catch (err) { process.env.NODE_ENV !== 'production' && console.error('Failed to upload picture:', err); toast.error('Failed to upload picture'); }
  };

  const handleAddFamily = async () => {
    if (!familyForm.name || !familyForm.phone) { toast.error('Please fill name and phone'); return; }
    try { await profileAPI.addFamily(familyForm.name, familyForm.phone); toast.success('Family member added'); setFamilyForm({ name: '', phone: '' }); setShowFamilyModal(false); loadProfile(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to add'); }
  };
  const handleRemoveFamily = async (id) => { try { await profileAPI.removeFamily(id); toast.success('Removed'); loadProfile(); } catch { toast.error('Failed to remove'); } };

  const handleSearchStaff = async (q) => {
    setStaffSearch(q);
    if (q.length < 1) { setStaffResults([]); return; }
    try { const { data } = await profileAPI.searchStaff(q); setStaffResults(data); } catch { setStaffResults([]); }
  };
  const handleAddDailyHelp = async (staffId) => {
    try { await profileAPI.addDailyHelp(staffId); toast.success('Daily help added'); setStaffSearch(''); setStaffResults([]); setShowHelpModal(false); loadProfile(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to add'); }
  };
  const handleRemoveDailyHelp = async (id) => { try { await profileAPI.removeDailyHelp(id); toast.success('Removed'); loadProfile(); } catch { toast.error('Failed to remove'); } };

  const handleAddVehicle = async () => {
    if (!vehicleForm.make || !vehicleForm.model || !vehicleForm.color || !vehicleForm.license_plate) { toast.error('Please fill all vehicle details'); return; }
    try { await profileAPI.addVehicle(vehicleForm); toast.success('Vehicle added'); setVehicleForm({ vehicle_type: '4-wheeler', make: '', model: '', color: '', license_plate: '', is_ev: false }); setShowVehicleModal(false); loadProfile(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to add'); }
  };
  const handleRemoveVehicle = async (id) => { try { await profileAPI.removeVehicle(id); toast.success('Removed'); loadProfile(); } catch { toast.error('Failed to remove'); } };

  const handleAddPet = async () => {
    if (!petForm.name) { toast.error('Please enter pet name'); return; }
    try { await profileAPI.addPet(petForm); toast.success('Pet added'); setPetForm({ pet_type: 'dog', breed: '', name: '' }); setShowPetModal(false); loadProfile(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to add'); }
  };
  const handleRemovePet = async (id) => { try { await profileAPI.removePet(id); toast.success('Removed'); loadProfile(); } catch { toast.error('Failed to remove'); } };

  const handleUploadVaccinationDoc = async (petId) => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/jpeg,image/png,image/webp,application/pdf';
    input.onchange = async (e) => {
      const file = e.target.files?.[0]; if (!file) return;
      if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
      const formData = new FormData(); formData.append('file', file);
      try { await profileAPI.uploadVaccinationDoc(petId, formData); toast.success('Vaccination document uploaded'); loadProfile(); }
      catch (error) { toast.error(error.response?.data?.detail || 'Failed to upload document'); }
    };
    input.click();
  };

  const handleViewVaccinationDoc = async (petId) => {
    // Open tab synchronously to keep the user-gesture token; otherwise popup blockers reject it.
    const newTab = window.open('', '_blank');
    if (!newTab) {
      toast.error('Pop-up blocked. Please allow pop-ups for this site.');
      return;
    }
    try {
      const url = profileAPI.getVaccinationDocUrl(petId);
      const token = getAuthToken();
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('Failed to fetch');
      const blob = await response.blob();
      newTab.location.href = URL.createObjectURL(blob);
    } catch {
      newTab.close();
      toast.error('Failed to view document');
    }
  };

  const handleUploadPetPhoto = async (petId) => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/jpeg,image/png,image/webp';
    input.onchange = async (e) => {
      const file = e.target.files?.[0]; if (!file) return;
      if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
      const formData = new FormData(); formData.append('file', file);
      try {
        const token = getAuthToken();
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/pets/${petId}/photo`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
        });
        if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Upload failed'); }
        toast.success('Pet photo uploaded'); loadProfile();
      } catch (error) { toast.error(error.message || 'Failed to upload photo'); }
    };
    input.click();
  };

  const getPetPhotoUrl = (petId) => {
    const token = getAuthToken();
    return { url: `${process.env.REACT_APP_BACKEND_URL}/api/profile/pets/${petId}/photo`, token };
  };

  const handleUploadVehiclePicture = async (vehicleId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await profileAPI.uploadVehiclePicture(vehicleId, formData);
      toast.success('Vehicle photo uploaded');
      loadProfile();
    } catch (err) {
      process.env.NODE_ENV !== 'production' && console.error('Vehicle photo upload error:', err);
      toast.error('Failed to upload vehicle photo');
    }
  };

  const handleSearchCommunity = async (q) => {
    setCommunitySearch(q);
    if (q.length < 1) { setCommunityResults([]); return; }
    try { const { data } = await profileAPI.searchCommunities(q); setCommunityResults(data); } catch { setCommunityResults([]); }
  };
  const handleRequestJoin = async (communityId) => {
    try { await profileAPI.requestCommunityJoin(communityId); toast.success('Join request sent! Awaiting management approval.'); setCommunitySearch(''); setCommunityResults([]); setShowCommunityModal(false); loadCommunities(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to send request'); }
  };

  const handleUpdateHobbies = async (newHobbies) => {
    try {
      const { data } = await profileAPI.updateHobbies(newHobbies);
      setHobbies(data.hobbies || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update hobbies');
    }
  };

  const handleAddCustomHobby = async (hobby) => {
    await handleUpdateHobbies([...hobbies, hobby]);
  };

  const handleToggleDirectory = async (value) => {
    const prev = showInDirectory;
    setShowInDirectory(value);
    try {
      await profileAPI.setDirectoryPreference({ show_in_directory: value });
      toast.success(value ? 'Added to neighbors directory' : 'Removed from neighbors directory');
    } catch (err) {
      setShowInDirectory(prev);
      toast.error(err.response?.data?.detail || 'Failed to update preference');
    }
  };

  const handleToggleAllowMessages = async (value) => {
    const prev = allowMessages;
    setAllowMessages(value);
    try { await chatAPI.setPreferences(value); toast.success(value ? 'Direct messages allowed' : 'Direct messages disabled'); }
    catch { setAllowMessages(prev); toast.error('Failed to update'); }
  };

  const handleToggleVisibility = async (key, value) => {
    const prev = directoryVisibility;
    const next = { ...directoryVisibility, [key]: value };
    setDirectoryVisibility(next);
    try {
      const params = { show_in_directory: showInDirectory };
      if (key === 'profile_picture') params.show_profile_picture = value;
      if (key === 'email') params.show_email = value;
      if (key === 'phone') params.show_phone = value;
      if (key === 'hobbies') params.show_hobbies = value;
      await profileAPI.setDirectoryPreference(params);
    } catch (err) {
      setDirectoryVisibility(prev);
      toast.error('Failed to update visibility');
    }
  };

  const goBack = () => {
    const role = user?.role;
    if (role === 'super_admin') navigate('/super-admin');
    else if (role === 'management') navigate('/management');
    else if (role === 'guard') navigate('/guard');
    else navigate('/resident');
  };

  const familyMembers = profile?.family_members || [];
  const dailyHelp = profile?.daily_help || [];
  const vehicles = profile?.vehicles || [];
  const pets = profile?.pets || [];
  const communityName = profile?.user?.community_name || communities?.current?.name || 'My Profile';

  if (loading) {
    return <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center"><p className="text-slate-500">Loading profile...</p></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-slate-900 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button data-testid="profile-back-button" onClick={goBack} variant="ghost" className="text-white hover:bg-slate-800" size="sm"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 data-testid="profile-header-title" className="text-lg font-bold">{communityName}</h1>
              <p className="text-xs text-slate-400">{profile?.user?.role?.toUpperCase()} Profile</p>
            </div>
          </div>
          <Button data-testid="add-community-header-button" size="sm" onClick={() => setShowCommunityModal(true)} className="bg-gk-teal hover:bg-gk-teal-hover">
            <Plus className="w-4 h-4 mr-1" /> Add Community
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <ProfileCard profile={profile} pictureUrl={pictureUrl} onUploadPicture={handleUploadPicture} />

        {/* Community Memberships */}
        <Card className="shadow-lg border-0" data-testid="communities-card">
          <CardHeader className="bg-gk-navy-light text-white rounded-t-xl flex flex-row items-center justify-between pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-white"><Building2 className="w-5 h-5" /> My Communities</CardTitle>
            <Button data-testid="add-community-button" size="sm" onClick={() => setShowCommunityModal(true)} className="bg-gk-teal hover:bg-gk-teal-hover"><Plus className="w-4 h-4 mr-1" /> Add</Button>
          </CardHeader>
          <CardContent className="pt-4">
            {communities.current && (
              <div className="p-3 bg-gk-bg rounded-lg border border-slate-200">
                <div className="flex items-center justify-between">
                  <div><p className="font-semibold text-sm">{communities.current.name}</p><p className="text-xs text-slate-500">{communities.current.address}</p></div>
                  <Badge className="bg-gk-teal">Current</Badge>
                </div>
              </div>
            )}
            {communities.approved.map((c) => (
              <div key={c.id} className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 mt-3">
                <div className="flex items-center justify-between">
                  <div><p className="font-semibold text-sm">{c.community_name}</p><p className="text-xs text-slate-500">{c.community_address}</p></div>
                  <Badge className="bg-emerald-600"><CheckCircle2 className="w-3 h-3 mr-1" />Approved</Badge>
                </div>
              </div>
            ))}
            {communities.pending.map((c) => (
              <div key={c.id} className="p-3 bg-amber-50 rounded-lg border border-amber-200 mt-3">
                <div className="flex items-center justify-between">
                  <div><p className="font-semibold text-sm">{c.community_name}</p><p className="text-xs text-slate-500">Requested {new Date(c.requested_at).toLocaleDateString()}</p></div>
                  <Badge variant="outline" className="border-amber-300 text-amber-600"><Clock className="w-3 h-3 mr-1" />Pending</Badge>
                </div>
              </div>
            ))}
            {!communities.current && communities.pending.length === 0 && communities.approved.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">No communities yet. Search and join one!</p>
            )}
          </CardContent>
        </Card>

        {user?.role === 'resident' && (
          <>
            <FamilyCard members={familyMembers} onAdd={() => setShowFamilyModal(true)} onRemove={handleRemoveFamily} />
            <PetsCard pets={pets} onAdd={() => setShowPetModal(true)} onRemove={handleRemovePet} onUploadPhoto={handleUploadPetPhoto} onUploadVaccinationDoc={handleUploadVaccinationDoc} onViewVaccinationDoc={handleViewVaccinationDoc} getPetPhotoUrl={getPetPhotoUrl} />
            <DailyHelpCard dailyHelp={dailyHelp} onAdd={() => setShowHelpModal(true)} onRemove={handleRemoveDailyHelp} />
            <VehiclesCard vehicles={vehicles} onAdd={() => setShowVehicleModal(true)} onRemove={handleRemoveVehicle} config={config} onUploadPicture={handleUploadVehiclePicture} />
            <HobbiesCard hobbies={hobbies} predefinedHobbies={predefinedHobbies} onChange={handleUpdateHobbies} onAddCustom={handleAddCustomHobby} />
            <DirectoryOptInCard
              showInDirectory={showInDirectory}
              onChange={handleToggleDirectory}
              enabled={!!config?.neighbors_directory_enabled}
              visibility={directoryVisibility}
              onToggleVisibility={handleToggleVisibility}
            />
            <ChatPreferenceCard allowMessages={allowMessages} onChange={handleToggleAllowMessages} />
            <PasskeyCard />
          </>
        )}

        {user?.role !== 'resident' && (
          <>
            <PasskeyCard />
            <Card className="shadow-lg border-0">
              <CardContent className="p-6 text-center">
                <Shield className="w-12 h-12 text-gk-teal mx-auto mb-3" />
                <p className="text-slate-600">
                  {user?.role === 'management' ? 'Management profiles will have additional settings in a future update.' :
                   user?.role === 'guard' ? 'Guard profiles will have shift schedules in a future update.' :
                   'Super Admin profile settings coming soon.'}
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </main>

      <FamilyModal open={showFamilyModal} onOpenChange={setShowFamilyModal} form={familyForm} setForm={setFamilyForm} onSubmit={handleAddFamily} />
      <PetModal open={showPetModal} onOpenChange={setShowPetModal} form={petForm} setForm={setPetForm} onSubmit={handleAddPet} />
      <DailyHelpModal open={showHelpModal} onOpenChange={setShowHelpModal} staffSearch={staffSearch} onSearch={handleSearchStaff} staffResults={staffResults} onAdd={handleAddDailyHelp} />
      <VehicleModal open={showVehicleModal} onOpenChange={setShowVehicleModal} form={vehicleForm} setForm={setVehicleForm} onSubmit={handleAddVehicle} />
      <CommunityModal open={showCommunityModal} onOpenChange={setShowCommunityModal} search={communitySearch} onSearch={handleSearchCommunity} results={communityResults} onJoin={handleRequestJoin} />
    </div>
  );
}
